/**
 * Wraps at the last space that fits, and hard-splits a word that never will.
 *
 * A product name is the case that matters: "REFRIGERANTE GUARANA 2L RETORNAVEL"
 * does not fit any thermal paper, and truncating it would leave two products
 * looking identical on the receipt a customer uses to complain.
 */
const wrap = (text, width) => {
    const lines = [];
    for (const paragraph of text.split('\n')) {
        let current = '';
        for (const word of paragraph.split(' ')) {
            if (word.length > width) {
                // A word longer than the paper: emit what is pending, then cut the word
                // into full lines. Rare, and silently wrong if unhandled.
                if (current.length > 0)
                    lines.push(current);
                let rest = word;
                while (rest.length > width) {
                    lines.push(rest.slice(0, width));
                    rest = rest.slice(width);
                }
                current = rest;
                continue;
            }
            const candidate = current.length === 0 ? word : `${current} ${word}`;
            if (candidate.length > width) {
                lines.push(current);
                current = word;
            }
            else {
                current = candidate;
            }
        }
        lines.push(current);
    }
    return lines;
};
const align = (line, width, how) => {
    if (line.length >= width)
        return line.slice(0, width);
    const slack = width - line.length;
    if (how === 'right')
        return ' '.repeat(slack) + line;
    if (how === 'center')
        return ' '.repeat(Math.floor(slack / 2)) + line;
    return line;
};
/**
 * A label on the left and a value on the right, on one line.
 *
 * **The label loses characters, never the value.** A receipt whose total reads
 * "R$ 1.234," is worse than one whose description reads "REFRIGERANTE GU" — the
 * first is wrong about money and the second is merely abbreviated. There is
 * always at least one space between them, so nothing runs together.
 */
export const pairLine = (label, value, width) => {
    const parts = pairParts(label, value, width);
    if (parts.label === '')
        return align(value, width, 'right');
    return (parts.label +
        ' '.repeat(width - parts.label.length - parts.value.length) +
        parts.value);
};
/**
 * O rótulo e o valor de uma linha, já cortados para caber — F160.
 *
 * A mesma decisão de `pairLine` (o rótulo perde letras, o valor nunca), mas
 * devolvendo as DUAS partes em vez de um texto só. É o que o papel em HTML
 * precisa para desenhá-las como dois elementos na mesma linha: com um texto
 * único separado por espaços, o driver da térmica da Elite Digital trocava a
 * fonte e jogava o valor uma linha para cima — a data ao lado do "Nº", o
 * cliente ao lado de "Data", o subtotal acima de "Subtotal".
 */
export const pairParts = (label, value, width) => {
    const room = width - value.length - 1;
    if (room <= 0)
        return { label: '', value };
    return { label: label.length > room ? label.slice(0, room) : label, value };
};
/**
 * The blocks of a document as lines of text.
 *
 * `big` doubles a character's width on the printer, so a big line fits HALF the
 * paper. Laying it out at the full width and letting the printer double it is
 * how a total ends up printed off the edge of the roll — so it is laid out at
 * half, and the encoder is told to double it.
 */
/**
 * Onde o papel se rasga, desenhado (F150).
 *
 * Tracejado, e não a linha cheia do `rule`: quem segura a bobina precisa
 * distinguir um separador de seção do lugar onde corta, e as duas coisas
 * aparecem no mesmo papel quando saem duas vias.
 */
const tearLine = (width) => Array.from({ length: Math.ceil(width / 2) }, () => '-').join(' ');
/**
 * De onde em diante só há corte.
 *
 * Um `cut` no FIM é o fim do papel, e todo construtor põe um lá — desenhá-lo
 * acrescentaria um tracejado ao pé de todo cupom que a loja já imprime. Um
 * `cut` no MEIO é outra coisa: é onde o operador separa uma via da outra, e
 * quem não o vê entrega as duas grudadas.
 */
const trailingCutFrom = (blocks) => {
    let from = blocks.length;
    while (from > 0 && blocks[from - 1]?.kind === 'cut')
        from -= 1;
    return from;
};
export const layout = (document, width) => {
    const lines = [];
    const tailFrom = trailingCutFrom(document.blocks);
    for (const [index, block] of document.blocks.entries()) {
        switch (block.kind) {
            case 'text': {
                const room = block.big === true ? Math.floor(width / 2) : width;
                for (const line of wrap(block.text, room)) {
                    lines.push({ text: align(line, room, block.align ?? 'left'), block });
                }
                break;
            }
            case 'pair': {
                lines.push({ text: pairLine(block.label, block.value, width), block });
                break;
            }
            case 'rule': {
                lines.push({ text: '-'.repeat(width), block });
                break;
            }
            case 'feed': {
                for (let index = 0; index < block.lines; index += 1) {
                    lines.push({ text: '', block });
                }
                break;
            }
            case 'barcode': {
                // Printed as its own data in text form: a preview cannot draw bars, and
                // a receipt whose barcode is unreadable is one where the number under
                // it is the whole point.
                lines.push({ text: align(block.data, width, 'center'), block });
                break;
            }
            case 'cut': {
                if (index < tailFrom) {
                    lines.push({ text: align(tearLine(width), width, 'center'), block });
                }
                break;
            }
            case 'table': {
                const widths = tableWidths(block, width);
                const row = (cells) => cells
                    .map((cell, column) => pad(fit(cell, widths[column] ?? 0), widths[column] ?? 0, block.columns[column]?.align ?? 'left'))
                    .join(' ')
                    .trimEnd();
                lines.push({
                    text: row(block.columns.map((column) => column.label)),
                    block,
                });
                lines.push({ text: '-'.repeat(width), block });
                for (const cells of block.rows)
                    lines.push({ text: row(cells), block });
                if (block.footer !== undefined) {
                    lines.push({ text: '-'.repeat(width), block });
                    lines.push({ text: row(block.footer), block });
                }
                break;
            }
        }
    }
    return lines;
};
/**
 * A célula ocupa a coluna inteira. Diferente de `align`, que deixa a linha
 * alinhada à esquerda sem espaço no fim — numa tabela o espaço no fim é o que
 * põe a coluna seguinte no lugar.
 */
const pad = (text, room, how) => {
    const slack = Math.max(0, room - text.length);
    if (how === 'right')
        return ' '.repeat(slack) + text;
    if (how === 'center') {
        const before = Math.floor(slack / 2);
        return ' '.repeat(before) + text + ' '.repeat(slack - before);
    }
    return text + ' '.repeat(slack);
};
/** Corta o texto que não cabe na coluna, sem reticências: coluna estreita. */
const fit = (text, room) => text.length <= room ? text : text.slice(0, Math.max(0, room));
/**
 * A largura de cada coluna na bobina (F170).
 *
 * A natural (o maior texto da coluna) quando cabe tudo; quando não cabe, quem
 * cede é a PRIMEIRA coluna — a descrição —, porque cortar um número de tamanho
 * ou uma quantidade seria imprimir outra grade. Se nem assim couber, as colunas
 * se repartem por igual e o texto é cortado.
 */
const tableWidths = (block, width) => {
    const all = [
        block.columns.map((column) => column.label),
        ...block.rows,
        ...(block.footer === undefined ? [] : [block.footer]),
    ];
    const natural = block.columns.map((_, column) => Math.max(0, ...all.map((cells) => (cells[column] ?? '').length)));
    const gaps = Math.max(0, block.columns.length - 1);
    const excess = natural.reduce((total, value) => total + value, 0) + gaps - width;
    if (excess <= 0)
        return natural;
    const first = natural[0] ?? 0;
    if (first - excess >= 3)
        return [first - excess, ...natural.slice(1)];
    const even = Math.max(1, Math.floor((width - gaps) / Math.max(1, block.columns.length)));
    return natural.map(() => even);
};
export const renderText = (document, width) => layout(document, width)
    .map((line) => line.text)
    .join('\n');
//# sourceMappingURL=text.js.map